"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import {
  Home,
  BarChart3,
  Play,
  Terminal,
  ChevronLeft,
  ShieldCheck,
  Library,
  Sliders,
  BookOpen,
  KeyRound,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { governancePageTitle } from "@/lib/governance-navigation";
import { useSidebar } from "@/contexts/sidebar-context";
import { useAuth } from "@/contexts/auth-context";
import { useWorkspace } from "@/contexts/workspace-context";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type PortalItem = {
  href: string;
  icon: typeof Home;
  labelKey?: string;
  customLabel?: string;
  protected: boolean;
  capabilities?: string[];
  requiresWorkspace?: boolean;
};

const navItems: { section: "overview" | "operations" | "management"; items: PortalItem[] }[] = [
  {
    section: "overview",
    items: [
      { href: "", icon: Home, labelKey: "home", protected: false },
      {
        href: "/analytics",
        icon: BarChart3,
        labelKey: "analytics",
        protected: true,
        capabilities: ["analytics.view_own", "analytics.view_platform"],
        requiresWorkspace: true,
      },
    ],
  },
  {
    section: "operations",
    items: [
      {
        href: "/runs",
        icon: Play,
        labelKey: "runs",
        protected: true,
        capabilities: ["runs.create", "runs.view_own", "runs.view_all"],
        requiresWorkspace: true,
      },
      {
        href: "/logs",
        icon: Terminal,
        labelKey: "logs",
        protected: true,
        capabilities: ["logs.view_own", "logs.view_all"],
        requiresWorkspace: true,
      },
    ],
  },
  {
    section: "management",
    items: [
      {
        href: "/prompt-governance",
        icon: BookOpen,
        protected: true,
        capabilities: ["prompts.view"],
        requiresWorkspace: true,
      },
      {
        href: "/config-governance",
        icon: Sliders,
        protected: true,
        capabilities: ["configs.view"],
        requiresWorkspace: true,
      },
      {
        href: "/content-safety",
        icon: ShieldCheck,
        protected: true,
        capabilities: ["content_safety.read", "content_safety.manage"],
        requiresWorkspace: true,
      },
      {
        href: "/feedback-curation",
        icon: BookOpen,
        protected: true,
        capabilities: ["feedback.read", "feedback.curate"],
        requiresWorkspace: true,
      },
      {
        href: "/library",
        icon: Library,
        labelKey: "library",
        protected: true,
        capabilities: ["prompts.view", "configs.view"],
        requiresWorkspace: true,
      },
      {
        href: "/api-keys",
        icon: KeyRound,
        customLabel: "API Keys",
        protected: true,
        capabilities: ["api_keys.manage_own"],
        requiresWorkspace: true,
      },
      {
        href: "/profile",
        icon: User,
        customLabel: "Profile",
        protected: true,
      },
    ],
  },
];

export function Sidebar() {
  const pathname = usePathname();
  const locale = useLocale();
  const t = useTranslations("nav");
  const tCommon = useTranslations("common");
  const tSections = useTranslations("sections");
  const { isCollapsed, toggleCollapsed, isMobileOpen, closeMobile } = useSidebar();
  const { isAuthenticated, hasCapability } = useAuth();
  const { workspaces, loading: workspaceLoading } = useWorkspace();
  const isRTL = locale === "ar";

  const isActive = (href: string) => {
    const fullPath = `/${locale}${href}`;
    if (href === "") {
      return pathname === `/${locale}` || pathname === `/${locale}/`;
    }
    return pathname.startsWith(fullPath);
  };

  return (
    <>
      {isMobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={closeMobile} />
      )}

      <aside
        className={cn(
          "fixed top-0 z-50 h-screen bg-sidebar border-sidebar-border flex flex-col transition-all duration-300",
          isRTL ? "right-0 border-l" : "left-0 border-r",
          isCollapsed ? "w-16" : "w-64",
          isMobileOpen
            ? "translate-x-0"
            : isRTL
              ? "translate-x-full lg:translate-x-0"
              : "-translate-x-full lg:translate-x-0",
        )}
      >
        <div className="flex h-16 items-center justify-between px-4 border-b border-sidebar-border">
          <Link href={`/${locale}`} className="flex items-center gap-3" onClick={closeMobile}>
            <img
              src="/image.png"
              alt="AI-Fabric"
              className={cn(
                "transition-all duration-300",
                isCollapsed ? "h-8 w-8 min-w-[32px]" : "h-9 w-auto",
              )}
            />
          </Link>
        </div>

        <button
          onClick={toggleCollapsed}
          className={cn(
            "absolute top-20 hidden lg:flex h-6 w-6 items-center justify-center rounded-full border border-sidebar-border bg-sidebar text-sidebar-foreground hover:bg-sidebar-accent transition-colors",
            isRTL ? "-left-3" : "-right-3",
          )}
          aria-label={tCommon("toggleSidebar")}
        >
          <ChevronLeft
            className={cn(
              "h-4 w-4 transition-transform",
              isCollapsed && (isRTL ? "-rotate-180" : "rotate-180"),
            )}
          />
        </button>

        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <TooltipProvider delayDuration={0}>
            {navItems.map((section) => {
              const visibleItems = section.items.filter((item) => {
                if (item.capabilities && !item.capabilities.some(hasCapability)) return false;
                if (item.requiresWorkspace && !workspaceLoading && workspaces.length === 0) return false;
                return true;
              });
              if (visibleItems.length === 0) return null;
              return (
                <div key={section.section} className="mb-6">
                  {!isCollapsed && (
                    <p className="mb-2 px-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {tSections(section.section)}
                    </p>
                  )}
                  <ul className="space-y-1">
                    {visibleItems.map((item) => {
                      const label =
                        governancePageTitle(item.href, locale) ||
                        (item.href === "/api-keys"
                          ? tCommon("apiKeys")
                          : item.href === "/profile"
                            ? tCommon("profile")
                            : item.customLabel || t(item.labelKey as "home"));
                      const Icon = item.icon;
                      const active = isActive(item.href);
                      const locked = item.protected && !isAuthenticated;
                      const linkContent = (
                        <Link
                          href={locked ? "#" : `/${locale}${item.href}`}
                          onClick={(event) => {
                            if (locked) event.preventDefault();
                            else closeMobile();
                          }}
                          className={cn(
                            "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                            active
                              ? "bg-sidebar-accent text-sidebar-accent-foreground"
                              : "text-sidebar-foreground hover:bg-sidebar-accent/50",
                            locked && "opacity-50 cursor-not-allowed",
                            isCollapsed && "justify-center",
                          )}
                        >
                          <Icon className="h-5 w-5 shrink-0" />
                          {!isCollapsed && <span>{label}</span>}
                        </Link>
                      );
                      return isCollapsed ? (
                        <li key={item.href}>
                          <Tooltip>
                            <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                            <TooltipContent side={isRTL ? "left" : "right"}>{label}</TooltipContent>
                          </Tooltip>
                        </li>
                      ) : (
                        <li key={item.href}>{linkContent}</li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </TooltipProvider>
        </nav>
      </aside>
    </>
  );
}
